"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { X, Clock, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { GET_OFFER, CategoryType } from "@/graphql/queries/get-offer";
import { useMounted } from "@/hooks/use-mounted";
import { cn } from "@/lib/utils";
import { useParams } from "next/navigation";
import { useCountry } from "@/hooks/use-country";
interface BannerTheme {
  /** Tailwind gradient classes applied to the banner wrapper */
  gradient: string;
  /** Inline style fallback for gradients that can't be expressed purely in Tailwind */
  gradientStyle?: React.CSSProperties;
  /** Text colour class (the whole banner inherits this) */
  textColor: string;
  /** Coupon chip border + bg */
  chipBorder: string;
  chipBg: string;
  chipHover: string;
  /** Label shown left of the divider (replaces "Special Offer") */
  label: string;
  /** Emoji shown next to the label */
  emoji: string;
  /** Shimmer overlay colour */
  shimmer: string;
}
const THEMES = {
  halloween: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(90deg, #1a0a00 0%, #7c2d00 40%, #c2410c 100%)"
    },
    textColor: "text-orange-100",
    chipBorder: "border-orange-400/40",
    chipBg: "bg-orange-500/20",
    chipHover: "hover:bg-orange-500/30",
    label: "Halloween",
    emoji: "🎃",
    shimmer: "rgba(251,146,60,0.12)"
  },
  diwali: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(90deg, #431407 0%, #92400e 35%, #b45309 70%, #d97706 100%)"
    },
    textColor: "text-amber-50",
    chipBorder: "border-yellow-300/50",
    chipBg: "bg-yellow-400/20",
    chipHover: "hover:bg-yellow-400/30",
    label: "Diwali",
    emoji: "🪔",
    shimmer: "rgba(251,191,36,0.15)"
  },
  christmas: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(90deg, #14532d 0%, #166534 40%, #991b1b 100%)"
    },
    textColor: "text-green-50",
    chipBorder: "border-red-400/40",
    chipBg: "bg-red-500/20",
    chipHover: "hover:bg-red-500/30",
    label: "Christmas",
    emoji: "🎄",
    shimmer: "rgba(248,113,113,0.12)"
  },
  blackfriday: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(90deg, #09090b 0%, #18181b 50%, #27272a 100%)"
    },
    textColor: "text-yellow-300",
    chipBorder: "border-yellow-400/50",
    chipBg: "bg-yellow-400/15",
    chipHover: "hover:bg-yellow-400/25",
    label: "Black Friday",
    emoji: "🛍️",
    shimmer: "rgba(250,204,21,0.10)"
  },
  cybermonday: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(90deg, #0c1445 0%, #1e3a8a 50%, #1d4ed8 100%)"
    },
    textColor: "text-cyan-100",
    chipBorder: "border-cyan-400/40",
    chipBg: "bg-cyan-400/15",
    chipHover: "hover:bg-cyan-400/25",
    label: "Cyber Monday",
    emoji: "💻",
    shimmer: "rgba(34,211,238,0.12)"
  },
  newyear: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(90deg, #0f172a 0%, #1e293b 40%, #334155 70%, #0f172a 100%)"
    },
    textColor: "text-yellow-200",
    chipBorder: "border-yellow-300/50",
    chipBg: "bg-yellow-300/15",
    chipHover: "hover:bg-yellow-300/25",
    label: "New Year",
    emoji: "🎆",
    shimmer: "rgba(253,224,71,0.15)"
  },
  valentine: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(90deg, #4c0519 0%, #9f1239 50%, #e11d48 100%)"
    },
    textColor: "text-pink-100",
    chipBorder: "border-pink-300/40",
    chipBg: "bg-pink-400/20",
    chipHover: "hover:bg-pink-400/30",
    label: "Valentine's",
    emoji: "❤️",
    shimmer: "rgba(244,114,182,0.12)"
  },
  eid: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(90deg, #064e3b 0%, #065f46 50%, #047857 100%)"
    },
    textColor: "text-emerald-100",
    chipBorder: "border-emerald-300/40",
    chipBg: "bg-emerald-400/20",
    chipHover: "hover:bg-emerald-400/30",
    label: "Eid Mubarak",
    emoji: "🌙",
    shimmer: "rgba(52,211,153,0.12)"
  },
  holi: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(90deg, #7e22ce 0%, #be185d 40%, #ea580c 70%, #ca8a04 100%)"
    },
    textColor: "text-white",
    chipBorder: "border-white/40",
    chipBg: "bg-white/15",
    chipHover: "hover:bg-white/25",
    label: "Holi",
    emoji: "🎨",
    shimmer: "rgba(255,255,255,0.12)"
  },
  independence: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(360deg, #046A38 0%, #046A38 33.33%,  #ffffff 33.33%, #ffffff 66.66%, #FF671F 66.66%, #FF671F 100%)"
    },
    textColor: "text-black",
    chipBorder: "border-white/40",
    chipBg: "bg-white/15",
    chipHover: "hover:bg-white/25",
    label: "Independence Day",
    emoji: "🇮🇳",
    shimmer: "rgba(255,255,255,0.10)"
  },
  summer: {
    gradient: "",
    gradientStyle: {
      background: "linear-gradient(90deg, #0369a1 0%, #0284c7 40%, #f59e0b 100%)"
    },
    textColor: "text-sky-50",
    chipBorder: "border-amber-300/40",
    chipBg: "bg-amber-400/20",
    chipHover: "hover:bg-amber-400/30",
    label: "Summer Sale",
    emoji: "☀️",
    shimmer: "rgba(251,191,36,0.12)"
  },
  default: {
    gradient: "bg-linear-to-r from-primary to-primary/80",
    gradientStyle: undefined,
    textColor: "text-primary-foreground",
    chipBorder: "border-white/30",
    chipBg: "bg-white/10",
    chipHover: "hover:bg-white/20",
    label: "Special Offer",
    emoji: "🏷️",
    shimmer: "rgba(255,255,255,0.08)"
  }
} satisfies Record<string, BannerTheme>;

// ─── Keyword → theme mapping ──────────────────────────────────────────────────

const KEYWORD_MAP: Array<{
  keys: string[];
  theme: keyof typeof THEMES;
}> = [{
  keys: ["halloween", "spooky", "trick", "treat", "ghost", "witch"],
  theme: "halloween"
}, {
  keys: ["diwali", "deepavali", "deepawali", "festival of lights", "diya"],
  theme: "diwali"
}, {
  keys: ["christmas", "xmas", "x-mas", "santa", "holiday season", "festive"],
  theme: "christmas"
}, {
  keys: ["black friday", "blackfriday"],
  theme: "blackfriday"
}, {
  keys: ["cyber monday", "cybermonday"],
  theme: "cybermonday"
}, {
  keys: ["new year", "newyear", "nye", "2025", "2026", "2027"],
  theme: "newyear"
}, {
  keys: ["valentine", "valentines", "love day"],
  theme: "valentine"
}, {
  keys: ["eid", "ramadan", "iftar"],
  theme: "eid"
}, {
  keys: ["holi", "colors", "colour festival"],
  theme: "holi"
}, {
  keys: ["independence", "republic day", "freedom"],
  theme: "independence"
}, {
  keys: ["summer", "monsoon", "spring"],
  theme: "summer"
}];
function detectTheme(offerName: string): BannerTheme {
  const lower = offerName.toLowerCase();
  for (const {
    keys,
    theme
  } of KEYWORD_MAP) {
    if (keys.some(k => lower.includes(k))) return THEMES[theme];
  }
  return THEMES.default;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}
const DISMISSED_KEY = "spoclearn-top-banner-dismissed";
function calcTimeLeft(endDateStr: string): TimeLeft | null {
  const diff = +new Date(endDateStr) - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor(diff / (1000 * 60 * 60) % 24),
    minutes: Math.floor(diff / 1000 / 60 % 60),
    seconds: Math.floor(diff / 1000 % 60)
  };
}
function pad(n: number) {
  return String(n).padStart(2, "0");
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TopOfferBanner() {
  const mounted = useMounted();
  const [dismissed, setDismissed] = useState(true);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
  const {
    course
  } = useParams<{
    course?: string;
  }>();
  const country = useCountry();
  const {
    data,
    loading,
    error
  } = useQuery(GET_OFFER, {
    variables: {
      type: CategoryType.ALL_COUNTRIES_ONLY_COUPON,
      pageSlug: course ?? null,
      countryCode: country
    },
    fetchPolicy: "cache-first"
  });
  const offer = data?.offerByCategory;

  // Detect theme from offer name
  const theme = useMemo(() => detectTheme(offer?.festival?.name ?? ""), [offer?.festival?.name]);

  // After mount, read session-based dismissal
  useEffect(() => {
    if (!mounted) return;
    setDismissed(sessionStorage.getItem(DISMISSED_KEY) === "1");
  }, [mounted]);

  // Countdown timer
  useEffect(() => {
    if (!offer?.endDate) return;
    setTimeLeft(calcTimeLeft(offer.endDate));
    const id = setInterval(() => {
      const t = calcTimeLeft(offer.endDate);
      setTimeLeft(t);
      if (!t) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [offer?.endDate]);

  // Drive layout offsets via a data attribute on <html>
  const isVisible = mounted && !dismissed && !loading && !error && !!offer && !!timeLeft;
  useEffect(() => {
    const root = document.documentElement;
    if (isVisible) {
      root.setAttribute("data-offer-banner", "visible");
    } else {
      root.removeAttribute("data-offer-banner");
    }
    return () => root.removeAttribute("data-offer-banner");
  }, [isVisible]);
  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  }, []);
  const handleCopy = useCallback(async () => {
    if (!offer?.couponCode) return;
    try {
      await navigator.clipboard.writeText(offer.couponCode);
      setCopied(true);
      toast.success("Coupon code copied!", {
        description: `Use ${offer.couponCode} at checkout.`
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy code.");
    }
  }, [offer?.couponCode]);
  if (!isVisible) return null;
  const showCountdown = timeLeft!.days < 7;
  return <div role="banner" aria-label="Special offer banner" className={cn("relative z-60 w-full overflow-hidden", theme.gradient, theme.textColor)} style={theme.gradientStyle}>
      {/* Shimmer overlay */}
      <div aria-hidden className="pointer-events-none absolute inset-0 animate-[shimmer_3s_linear_infinite] bg-size-[200%_100%]" style={{
      backgroundImage: `linear-gradient(105deg, transparent 40%, ${theme.shimmer} 50%, transparent 60%)`
    }} />

      {/* Floating decorative emoji — faint, large, right side */}
      <span aria-hidden className="pointer-events-none absolute top-1/2 -right-2 -translate-y-1/2 text-4xl opacity-10 blur-[1px] select-none">
        {theme.emoji}
      </span>

      <div className="relative mx-auto flex max-w-7xl items-center px-10 py-2 sm:px-12">
        {/* All content centred */}
        <div className="flex flex-1 flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center">
          {/* Occasion label */}
          <div className="flex shrink-0 items-center gap-1.5 opacity-95">
            <span aria-hidden className="text-sm leading-none">
              {theme.emoji}
            </span>
            <span className="hidden text-[11px] font-bold tracking-widest uppercase sm:inline">
              {theme.label}
            </span>
          </div>

          {/* Divider */}
          <span className="hidden opacity-30 sm:inline">·</span>

          {/* Offer name */}
          <p className="text-xs font-semibold sm:text-sm">{offer!.name}</p>

          {/* Coupon code chip */}
          {offer!.couponCode && <button onClick={handleCopy} title="Click to copy coupon code" className={cn("group flex items-center gap-1 rounded-md border px-2 py-0.5", "font-mono text-xs font-bold tracking-wider", "transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none", theme.chipBorder, theme.chipBg, theme.chipHover)}>
              {copied ? <Check className="size-3 shrink-0" aria-hidden /> : <Copy className="size-3 shrink-0" aria-hidden />}
              <span>{offer!.couponCode}</span>
            </button>}

          {/* Countdown — last 7 days only */}
          {showCountdown && <div className="flex items-center gap-1 text-xs opacity-90">
              <Clock className="size-3 shrink-0 animate-pulse" aria-hidden />
              <span className="font-mono font-semibold tabular-nums">
                {timeLeft!.days > 0 && <>{pad(timeLeft!.days)}d&nbsp;</>}
                {pad(timeLeft!.hours)}h&nbsp;{pad(timeLeft!.minutes)}m&nbsp;{pad(timeLeft!.seconds)}
                s
              </span>
            </div>}
        </div>

        {/* Close — absolute so it never shifts centred content */}
        <button onClick={handleDismiss} aria-label="Dismiss offer banner" className="absolute right-3 flex shrink-0 items-center justify-center rounded-full p-1 opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none">
          <X className="size-3.5" aria-hidden />
        </button>
      </div>
    </div>;
}